<template>
  <ConfirmDialog />
  <v-app v-if="newCelusVersion">
    <NewCelusVersionDialog></NewCelusVersionDialog>
  </v-app>
  <router-view v-else-if="$route.meta.outsideNormalLayout"> </router-view>
  <InvalidUserPage v-else-if="invalidUser"></InvalidUserPage>
  <EmailNotVerified
    v-else-if="
      user &&
      !user.impersonator &&
      !emailVerified &&
      (!user.is_superuser || otpRequired)
    "
  ></EmailNotVerified>
  <StandardLayout
    v-else-if="bootUpFinished && !showLoginDialog && !otpRequired"
  ></StandardLayout>
  <div v-else>
    <v-app>
      <BootUpWidget></BootUpWidget>
      <LoginDialog v-if="showLoginDialog"></LoginDialog>
      <OtpDialog v-else-if="otpRequired"></OtpDialog>
    </v-app>
  </div>
</template>

<script>
import LoginDialog from "@/components/account/LoginDialog";
import OtpDialog from "@/components/account/OtpDialog";
import BootUpWidget from "@/components/BootUpWidget";
import ConfirmDialog from "@/components/util/ConfirmDialog";
import NewCelusVersionDialog from "@/components/NewCelusVersionDialog";
import { mapActions, mapGetters, mapState } from "vuex";
import EmailNotVerified from "./EmailNotVerified";
import InvalidUserPage from "./InvalidUserPage";
import StandardLayout from "./StandardLayout";

export default {
  name: "Dashboard",
  components: {
    LoginDialog,
    OtpDialog,
    BootUpWidget,
    ConfirmDialog,
    InvalidUserPage,
    NewCelusVersionDialog,
    StandardLayout,
    EmailNotVerified,
  },
  data() {
    return {};
  },
  computed: {
    ...mapState({
      invalidUser: "invalidUser",
      user: "user",
      showLoginDialog: "showLoginDialog",
      otpRequired: "otpRequired",
      newCelusVersion: "newCelusVersion",
    }),
    ...mapGetters({
      loggedIn: "loggedIn",
      bootUpFinished: "bootUpFinished",
      emailVerified: "emailVerified",
    }),
    appLanguage: {
      get() {
        return this.$store.state.appLanguage;
      },
      set(newValue) {
        this.$store.dispatch("setAppLanguage", { lang: newValue });
        this.$router.go();
      },
    },
  },
  methods: {
    ...mapActions({
      start: "start",
    }),
  },
  created() {
    this.start();
  },
  async mounted() {
    this.$i18n.locale = this.appLanguage;
  },
  watch: {
    appLanguage() {
      this.$i18n.locale = this.appLanguage;
    },
  },
};
</script>

<style lang="scss">
img.avatar {
  font-size: 1.25rem;
}

section.header {
  margin-bottom: 2rem;
}

div.fields {
  margin-top: 1rem;
}

div.v-input {
  &.short {
    flex-grow: 0;
  }

  div.v-input__control {
    div.v-text-field__details {
      min-height: 1px;
      .v-messages {
        min-height: 1px;
      }
    }
  }
}

.subdued {
  color: #888888;
}

.top-dialog {
  position: absolute !important;
  top: 0 !important;

  @media screen and (min-height: 720px) {
    top: 2vh !important;
  }
}

table.overview-card {
  padding: 1rem;
  th {
    font-size: 16px;
  }
  tr.header {
    th {
      font-variant: small-caps;
      font-weight: 500;
      border-bottom: solid 1px #dddddd;
      padding-top: 0.5rem;
    }
    &:first-child {
      th {
        padding-top: 0;
      }
    }
  }
  td {
    font-size: 16px;
  }

  th {
    text-align: left;
    padding-right: 1.5rem;
    font-weight: 300;

    &.subdued-th {
      //font-weight: normal;
      color: #999999;
    }
  }
}

table.overview {
  th {
    text-align: left;
    padding-right: 1.5rem;
  }

  &.full-width {
    width: 100%;
  }
}

/* data and time formatting */
span.time {
  font-weight: 300;
  font-size: 87.5%;
}

ul.no-bullets {
  list-style: none;
  padding-left: 0;
}

.markdown {
  h3 {
    margin-bottom: 0.5rem;
  }
  h4 {
    margin-top: 1.5rem;
    margin-bottom: 0.5rem;
  }
  p {
    margin-bottom: 1rem;
  }
}

.chart-value {
  font-weight: bold;
  color: #555555;
  text-align: right;
  padding-left: 1rem;
}

.chart-missing-data {
  color: #ff6633;
}

#date-range-selector {
  transition:
    background-color 0.2s,
    border 0.2s;
  border: solid 4px transparent;
  border-radius: 6px;
}

ul.unobtrusive-bullets {
  list-style-type: square;
  padding: 0 0 0 1rem;
  margin: 0;
}

th.align-top {
  vertical-align: top;
}

td.auto-width,
th.auto-width {
  width: auto;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.auto-table .v-data-table__wrapper table {
  th:last-child,
  td:last-child {
    width: 100%;
  }

  th:not(:last-child),
  td:not(:last-child) {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 6rem;

    &:first-child {
      min-width: 1.5rem;
    }
  }

  th.text-right {
    // only tds in right aligned columns should be right aligned, not the ths
    text-align: left !important;
    display: flex;
    align-items: center;
    justify-content: space-around;
  }
}

.v-tab {
  text-transform: uppercase;
}

.small-caps {
  font-variant: small-caps;
}

.v-alert.info--text {
  a {
    // make links in info alerts bold and use the info color
    // (the primary color clashes with the alert color)
    color: var(--v-info) !important;
    font-weight: bold;
  }
}

.text-normal {
  font-size: 1rem;
}

.tab-active {
  color: #ffffffff !important;
  background-color: rgba(255, 255, 255, 0.2) !important;
}

.tab-inactive {
  color: rgba(255, 255, 255, 0.6) !important;
}
</style>
